import { supabase } from '../config/supabase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface BillingPortalResponse {
  success?: boolean;
  data?: {
    url?: string;
  };
  url?: string;
  message?: string;
  error?: {
    code?: string;
    message?: string;
    userMessage?: string;
  };
}

/**
 * Open the Stripe Customer Portal for the authenticated user.
 * Automatically fetches the current Supabase session and redirects the browser.
 */
export const openStripePortal = async (returnUrl: string) => {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    throw new Error('Unable to verify authentication. Please try again.');
  }

  const accessToken = data.session?.access_token;

  if (!accessToken) {
    throw new Error('You must be signed in to manage billing.');
  }

  const response = await fetch(`${API_BASE_URL}/api/billing/portal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ returnUrl }),
  });

  if (!response.ok) {
    let message = 'Failed to open billing portal.';
    let errorCode: string | undefined;

    try {
      const payload: BillingPortalResponse = await response.json();

      // Use userMessage if available (more user-friendly), fallback to message, then error message
      if (payload.error?.userMessage) {
        message = payload.error.userMessage;
        errorCode = payload.error.code;
      } else if (payload.error?.message) {
        message = payload.error.message;
        errorCode = payload.error.code;
      } else if (payload.message) {
        message = payload.message;
      }
    } catch (parseError) {
      // Ignore JSON parse errors and use default message
    }

    // Create error object with code for better handling upstream
    const error = new Error(message) as Error & { code?: string };
    if (errorCode) {
      error.code = errorCode;
    }
    throw error;
  }

  const payload: BillingPortalResponse = await response.json();
  const portalUrl = payload?.data?.url || payload?.url;

  if (!portalUrl) {
    throw new Error('Billing portal link is unavailable. Please contact support.');
  }

  window.location.href = portalUrl;
};
